import { type CreateContactFormData, createContactSchema } from '@/components/contacts/schema';
import { Icons } from '@/components/icon';
import { Company, CompanyPopover } from '@/components/table/cells/company';
import { Phone, PhonePopover } from '@/components/table/cells/phone';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import {
  Modal,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { H4, P3 } from '@/components/ui/text';
import { useTranslation } from '@/hooks/use-translation';
import type { components } from '@/services/api-allo/api-allo-v1';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCreateContactMutation } from '../queries/use-contact-mutations';

type Contact = components['schemas']['Contact'];
type CompanyResponse = components['schemas']['CompanyResponse'];

export interface ContactFormData {
  name: string;
  last_name: string;
  numbers: string[];
  company_id?: string | null;
  emails?: string[] | null;
  website?: string | null;
  job_title?: string | null;
  addresses?: string[] | null;
}

interface CreateContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (contact: Contact) => void;
  /** Optional custom submit handler. If provided, it will be called instead of the default mutation. */
  onCustomSubmit?: (data: ContactFormData) => void;
  /** Whether the custom submit is currently pending */
  isCustomSubmitPending?: boolean;
  /** Optional phone number to prefill in the form */
  defaultPhoneNumber?: string;
  /** Optional name to prefill in the form */
  defaultName?: string;
}

export function CreateContactModal({
  open,
  onOpenChange,
  onSuccess,
  onCustomSubmit,
  isCustomSubmitPending,
  defaultPhoneNumber,
  defaultName,
}: CreateContactModalProps) {
  const { t } = useTranslation('desktop_ui');

  const [phonePopoverOpen, setPhonePopoverOpen] = useState(false);
  const [companyPopoverOpen, setCompanyPopoverOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyResponse | null>(null);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const contentTopRef = useRef<HTMLDivElement>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<CreateContactFormData>({
    resolver: zodResolver(createContactSchema),
    defaultValues: {
      name: defaultName ?? '',
      phoneNumbers: defaultPhoneNumber ? [defaultPhoneNumber] : [],
      companyId: '',
      email: '',
      website: '',
      job_title: '',
      address: '',
    },
    mode: 'onSubmit',
  });

  // Update form defaults when defaultPhoneNumber or defaultName changes
  useEffect(() => {
    if (open && (defaultPhoneNumber || defaultName)) {
      reset({
        name: defaultName ?? '',
        phoneNumbers: defaultPhoneNumber ? [defaultPhoneNumber] : [],
        companyId: '',
        email: '',
        website: '',
        job_title: '',
        address: '',
      });
    }
  }, [open, defaultPhoneNumber, defaultName, reset]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      reset();
      setPhonePopoverOpen(false);
      setCompanyPopoverOpen(false);
      setSelectedCompany(null);
      setShowDiscardConfirmation(false);
    }
  }, [open, reset]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardConfirmation(true);
    } else {
      onOpenChange(false);
    }
  }, [isDirty, onOpenChange]);

  const handleConfirmDiscard = useCallback(() => {
    setShowDiscardConfirmation(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const createContactMutation = useCreateContactMutation({
    onSuccess: (contact) => {
      onSuccess?.(contact);
      onOpenChange(false);
    },
  });

  const onSubmit = useCallback(
    (data: CreateContactFormData) => {
      const nameParts = data.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Auto-add https:// to website if needed
      let website = data.website?.trim() || '';
      if (website && !/^https?:\/\//i.test(website)) {
        website = `https://${website}`;
      }

      const formData: ContactFormData = {
        name: firstName,
        last_name: lastName,
        numbers: data.phoneNumbers,
        company_id: data.companyId || null,
        emails: data.email?.trim() ? [data.email.trim()] : null,
        website: website || null,
        job_title: data.job_title?.trim() || null,
        addresses: data.address?.trim() ? [data.address.trim()] : null,
      };

      // Use custom submit handler if provided
      if (onCustomSubmit) {
        onCustomSubmit(formData);
        return;
      }

      createContactMutation.mutate({
        body: formData,
      });
    },
    [createContactMutation, onCustomSubmit],
  );

  const scrollToTop = useCallback(() => {
    const viewport = contentTopRef.current
      ?.closest('[data-slot="modal-content"]')
      ?.querySelector('[data-radix-scroll-area-viewport]');
    viewport?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleConfirm = useCallback(() => {
    handleSubmit(onSubmit, scrollToTop)();
  }, [handleSubmit, onSubmit, scrollToTop]);

  const handleModalOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && isDirty) {
        setShowDiscardConfirmation(true);
      } else {
        onOpenChange(newOpen);
      }
    },
    [isDirty, onOpenChange],
  );

  return (
    <Modal open={open} onOpenChange={handleModalOpenChange}>
      <ModalContainer>
        <ModalHeader
          icon={Icons.AddressBook}
          title={t('components.contacts.createModal.title')}
          onClose={handleClose}
        />
        <ModalContent>
          <div ref={contentTopRef} />
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <FormField
                className="mb-3"
                label={t('components.contacts.createModal.name')}
                required
                error={!!errors.name}
              >
                <input
                  type="text"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('components.contacts.createModal.namePlaceholder')}
                  className="w-full bg-transparent font-inter text-s font-medium text-primary-100 outline-none placeholder:text-primary-40"
                />
              </FormField>
            )}
          />

          <Controller
            control={control}
            name="phoneNumbers"
            render={({ field }) => (
              <PhonePopover
                phoneNumbers={field.value}
                open={phonePopoverOpen}
                onOpenChange={setPhonePopoverOpen}
                onAddPhone={(phone) => {
                  field.onChange([...field.value, phone]);
                }}
                onDeletePhone={(phone) => {
                  field.onChange(field.value.filter((p) => p !== phone));
                }}
                sideOffset={-30}
              >
                <FormField
                  className="mb-3"
                  label={t('components.contacts.createModal.phoneNumbers')}
                  required
                  error={!!errors.phoneNumbers}
                >
                  {field.value.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => setPhonePopoverOpen(true)}
                      className="w-full cursor-pointer text-left font-inter text-s font-medium text-primary-40 outline-none"
                    >
                      {t('components.contacts.createModal.phoneNumbersPlaceholder')}
                    </button>
                  ) : (
                    <div className="group flex w-full items-center gap-1">
                      <div className="flex flex-1 flex-wrap items-center gap-1 overflow-hidden">
                        {field.value.map((phone) => (
                          <Phone key={phone} phoneNumber={phone} tabIndex={-1} />
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setPhonePopoverOpen(true)}
                        className="shrink-0 cursor-pointer font-inter text-s font-medium text-primary-40 opacity-0 transition-opacity group-hover:opacity-100 hover:text-primary-60"
                      >
                        {t('components.contacts.createModal.edit')}
                      </button>
                    </div>
                  )}
                </FormField>
              </PhonePopover>
            )}
          />

          <Controller
            control={control}
            name="companyId"
            render={({ field }) => (
              <CompanyPopover
                company={selectedCompany}
                open={companyPopoverOpen}
                onOpenChange={setCompanyPopoverOpen}
                onSelectCompany={(company) => {
                  setSelectedCompany(company);
                  field.onChange(company?.id ?? '');
                }}
                sideOffset={-30}
              >
                <FormField className="mb-3" label={t('components.contacts.createModal.company')}>
                  {!selectedCompany ? (
                    <button
                      type="button"
                      onClick={() => setCompanyPopoverOpen(true)}
                      className="w-full cursor-pointer text-left font-inter text-s font-medium text-primary-40 outline-none"
                    >
                      {t('components.contacts.createModal.companyPlaceholder')}
                    </button>
                  ) : (
                    <div className="group flex w-full items-center gap-1">
                      <div className="flex flex-1 items-center overflow-hidden">
                        <Company company={selectedCompany} tabIndex={-1} underlined={false} />
                      </div>
                      <button
                        type="button"
                        onClick={() => setCompanyPopoverOpen(true)}
                        className="shrink-0 cursor-pointer font-inter text-s font-medium text-primary-40 opacity-0 transition-opacity group-hover:opacity-100 hover:text-primary-60"
                      >
                        {t('components.contacts.createModal.edit')}
                      </button>
                    </div>
                  )}
                </FormField>
              </CompanyPopover>
            )}
          />

          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <FormField
                className="mb-3"
                label={t('components.contacts.createModal.email')}
                error={!!errors.email}
              >
                <input
                  type="email"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('components.contacts.createModal.emailPlaceholder')}
                  className="w-full bg-transparent font-inter text-s font-medium text-primary-100 outline-none placeholder:text-primary-40"
                />
              </FormField>
            )}
          />

          <Controller
            control={control}
            name="website"
            render={({ field }) => (
              <FormField
                className="mb-3"
                label={t('components.contacts.createModal.website')}
                error={!!errors.website}
              >
                <input
                  type="text"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('components.contacts.createModal.websitePlaceholder')}
                  className="w-full bg-transparent font-inter text-s font-medium text-primary-100 outline-none placeholder:text-primary-40"
                />
              </FormField>
            )}
          />

          <Controller
            control={control}
            name="job_title"
            render={({ field }) => (
              <FormField className="mb-3" label={t('components.contacts.createModal.jobTitle')}>
                <input
                  type="text"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('components.contacts.createModal.jobTitlePlaceholder')}
                  className="w-full bg-transparent font-inter text-s font-medium text-primary-100 outline-none placeholder:text-primary-40"
                />
              </FormField>
            )}
          />

          <Controller
            control={control}
            name="address"
            render={({ field }) => (
              <FormField className="mb-3" label={t('components.contacts.createModal.address')}>
                <input
                  type="text"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('components.contacts.createModal.addressPlaceholder')}
                  className="w-full bg-transparent font-inter text-s font-medium text-primary-100 outline-none placeholder:text-primary-40"
                />
              </FormField>
            )}
          />
        </ModalContent>
        <ModalFooter
          primaryLabel={t('components.contacts.form.create')}
          onConfirm={handleConfirm}
          disabled={createContactMutation.isPending || isCustomSubmitPending}
        />
      </ModalContainer>

      <Dialog open={showDiscardConfirmation} onOpenChange={setShowDiscardConfirmation}>
        <DialogContent
          className="w-[400px] overflow-hidden rounded-2xl p-0 gap-0"
          showCloseIcon={false}
        >
          <DialogHeader className="gap-1 px-4 py-3">
            <DialogTitle>
              <H4 className="text-primary-100">
                {t('components.contacts.form.discardConfirmation.title')}
              </H4>
            </DialogTitle>
            <DialogDescription>
              <P3 className="text-primary-60">
                {t('components.contacts.form.discardConfirmation.description')}
              </P3>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 border-t border-primary-5 p-3">
            <button
              type="button"
              onClick={() => setShowDiscardConfirmation(false)}
              className="cursor-pointer flex h-6 items-center justify-center rounded-md border border-primary-10 bg-white px-2 hover:bg-primary-5 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-40"
            >
              <P3 className="text-primary-100">
                {t('components.contacts.form.discardConfirmation.cancel')}
              </P3>
            </button>
            <button
              type="button"
              onClick={handleConfirmDiscard}
              className="cursor-pointer h-6 flex items-center justify-center rounded-md border border-status-danger bg-extra-decline px-2 hover:bg-extra-decline"
            >
              <P3 className="text-white">
                {t('components.contacts.form.discardConfirmation.confirm')}
              </P3>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </Modal>
  );
}
